import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Sparkles, Zap, Lock } from 'lucide-react'
import { CREDITS_UPDATE_EVENT, CREDITS_EXHAUSTED_EVENT, type CreditsUpdateDetail } from '@/lib/api'

// Deliberately self-contained (not built on the shared useToast()/<Toaster/> pair) —
// that hook holds its toast list in per-instance component state with no shared store,
// so a toast() call from any page never actually reaches the <Toaster/> mounted once in
// App.tsx. Rather than depend on that, this renders its own small banner + modal.

const SEEN_KEY_PREFIX = 'sycek:freemium-nudge-seen:'

function markSeen(threshold: number) {
  try { sessionStorage.setItem(`${SEEN_KEY_PREFIX}${threshold}`, '1') } catch { /* storage unavailable — worst case re-shows once */ }
}
function alreadySeen(threshold: number): boolean {
  try { return sessionStorage.getItem(`${SEEN_KEY_PREFIX}${threshold}`) === '1' } catch { return false }
}

export function FreemiumNudgeProvider() {
  const [banner, setBanner] = useState<{ kind: 'welcome' | 'soft'; used: number; limit: number } | null>(null)
  const [hardStop, setHardStop] = useState(false)

  useEffect(() => {
    const onCreditsUpdate = (e: Event) => {
      const { used, limit } = (e as CustomEvent<CreditsUpdateDetail>).detail
      if (used === 1 && !alreadySeen(1)) {
        markSeen(1)
        setBanner({ kind: 'welcome', used, limit })
      } else if (used === 3 && !alreadySeen(3)) {
        markSeen(3)
        setBanner({ kind: 'soft', used, limit })
      }
    }
    const onExhausted = () => setHardStop(true)

    window.addEventListener(CREDITS_UPDATE_EVENT, onCreditsUpdate)
    window.addEventListener(CREDITS_EXHAUSTED_EVENT, onExhausted)
    return () => {
      window.removeEventListener(CREDITS_UPDATE_EVENT, onCreditsUpdate)
      window.removeEventListener(CREDITS_EXHAUSTED_EVENT, onExhausted)
    }
  }, [])

  useEffect(() => {
    if (!banner) return
    const t = setTimeout(() => setBanner(null), 8000)
    return () => clearTimeout(t)
  }, [banner])

  return (
    <>
      {banner && (
        <div className="fixed bottom-4 right-4 z-[100] max-w-sm">
          <div className="rounded-lg border border-green-500/30 bg-[#0d0d1a] shadow-lg shadow-black/40 p-4 flex gap-3">
            {banner.kind === 'welcome' ? (
              <Sparkles className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
            ) : (
              <Zap className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              {banner.kind === 'welcome' ? (
                <>
                  <div className="text-sm font-semibold text-white">Welcome to Sycek OSINT</div>
                  <p className="text-xs text-gray-400 mt-1">
                    That's 1 of your {banner.limit} free queries used — you have {banner.limit - banner.used} more on the house.
                  </p>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-white">Running low on free queries</div>
                  <p className="text-xs text-gray-400 mt-1">
                    {banner.limit - banner.used} left on the free plan. Upgrading unlocks the rest of the platform — more modules, deeper attribution, and export.
                  </p>
                </>
              )}
              <Link to="/pricing" className="text-xs text-green-400 hover:text-green-300 underline mt-2 inline-block">
                View plans
              </Link>
            </div>
            <button
              onClick={() => setBanner(null)}
              className="text-gray-500 hover:text-gray-300 text-xs shrink-0"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <Dialog open={hardStop} onOpenChange={setHardStop}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-yellow-500/10 mb-2">
              <Lock className="w-6 h-6 text-yellow-400" />
            </div>
            <DialogTitle>Free plan limit reached</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-gray-400">
              You've used all of your free queries. Upgrade to keep investigating — plans start at $499/mo,
              or talk to us directly if you're evaluating this for a team or agency.
            </p>
            <div className="flex flex-col gap-2">
              <Link to="/pricing" onClick={() => setHardStop(false)}>
                <Button className="w-full bg-green-500 hover:bg-green-600 text-black">
                  View Plans & Upgrade
                </Button>
              </Link>
              <a href="mailto:support@sycek.io?subject=Freemium%20limit%20reached%20-%20book%20a%20call">
                <Button variant="outline" className="w-full">
                  Book a Call
                </Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default FreemiumNudgeProvider
